import { accountColorSchema, categoryIconSchema, categoryBalanceSchema, categorySchema, frequentItemsSchema, parseApiResponse } from '@/api/responseSchemas'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { toLocalYMD } from '@/utils/dateUtils'
import { useAuthStore } from '@/stores/useAuthStore'
import { captureEntityMutation } from '@/analytics/posthog'

export interface Category {
    id: number
    name: string
    description?: string
    icon_id?: number | null
    color_id?: number | null
    type?: string
}

export type FrequentCategories = Record<string, number>

export interface CategoryBalance {
    id: number
    name: string
    description?: string
    icon_id?: number | null
    color_id?: number | null
    category_total_expense: number
    category_total_deposit: number
    percentage_of_total_expense: number
    percentage_of_total_deposit: number
}

export interface CategoryBalanceResponse {
    total_deposit: number
    total_expense: number
    count: number
    categories_balance: CategoryBalance[]
    /** Spend with no category, or whose category was deleted. Already inside the totals. */
    uncategorized_total_expense: number
    uncategorized_total_deposit: number
}

interface CategoryInput {
    name: string
    description?: string
    icon_id?: number | null
    color_id?: number | null
}

interface CategoriesBalanceQueryParams {
    billable_date_gte?: string
    billable_date_lte?: string
    category_total_expense?: "asc" | "desc" | string
    monthly?: boolean
}

export const useCategoryIcons = (options?: { enabled?: boolean }) => {
    const token = useAuthStore((state) => state.token)
    return useQuery({
        queryKey: ['categoryIcons'],
        queryFn: async () => {
            const res = await apiClient.get('/categories/icons')
            return parseApiResponse(categoryIconSchema.array(), res.data, "GET /categories/icons")
        },
        enabled: !!token && (options?.enabled ?? true),
        staleTime: Infinity,
    })
}

export const useCategoryColors = (options?: { enabled?: boolean }) => {
    const token = useAuthStore((state) => state.token)
    return useQuery({
        queryKey: ['categoryColors'],
        queryFn: async () => {
            const res = await apiClient.get('/categories/colors')
            return parseApiResponse(accountColorSchema.array(), res.data, "GET /categories/colors")
        },
        enabled: !!token && (options?.enabled ?? true),
        staleTime: Infinity,
    })
}

// Query hooks
export const useCategoriesBalance = (params?: CategoriesBalanceQueryParams) => {
    const token = useAuthStore((s) => s.token)
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)

    const shouldUseMonthlyFlag = params?.monthly === true
    const effectiveParams: CategoriesBalanceQueryParams = shouldUseMonthlyFlag
        ? {
            category_total_expense: params?.category_total_expense ?? "desc",
            monthly: true,
            ...(params?.billable_date_gte ? { billable_date_gte: params.billable_date_gte } : {}),
            ...(params?.billable_date_lte ? { billable_date_lte: params.billable_date_lte } : {}),
        }
        : {
            billable_date_gte: params?.billable_date_gte ?? toLocalYMD(startOfMonth),
            billable_date_lte: params?.billable_date_lte ?? toLocalYMD(endOfMonth),
            category_total_expense: params?.category_total_expense ?? "desc",
        }

    return useQuery({
        queryKey: ['categoriesBalance', effectiveParams],
        queryFn: async () => {
            const res = await apiClient.get('/categories/balance', {
                params: effectiveParams,
            })
            return parseApiResponse(categoryBalanceSchema, res.data, "GET /categories/balance")
        },
        enabled: !!token,
    })
}

export const useCategories = (options?: { enabled?: boolean }) => {
    const token = useAuthStore((s) => s.token)

    return useQuery({
        queryKey: ['categories'],
        queryFn: async () => {
            const res = await apiClient.get('/categories/')
            return parseApiResponse(categorySchema.array(), res.data, "GET /categories")
        },
        enabled: !!token && (options?.enabled ?? true),
    })
}

export const useFrequentCategories = (options?: { enabled?: boolean }) => {
    const token = useAuthStore((s) => s.token)

    return useQuery<FrequentCategories, unknown>({
        queryKey: ['frequentCategories'],
        queryFn: async () => {
            const res = await apiClient.get('/categories/frequent')
            return parseApiResponse(frequentItemsSchema, res.data, "GET /categories/frequent")
        },
        enabled: !!token && (options?.enabled ?? true),
    })
}

// Mutation hooks
export const useCreateCategory = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (categoryData: CategoryInput) => {
            const dataToSend = {
                name: categoryData.name,
                ...(categoryData.description && { description: categoryData.description }),
                ...(categoryData.icon_id !== undefined && { icon_id: categoryData.icon_id }),
                ...(categoryData.color_id !== undefined && { color_id: categoryData.color_id })
            }
            const res = await apiClient.post('/categories/', dataToSend)
            return res.data
        },
        onSuccess: () => {
            captureEntityMutation({ entity: 'category', operation: 'create', result: 'success' })
            queryClient.invalidateQueries({ queryKey: ['categories'] })
            queryClient.invalidateQueries({ queryKey: ['categoriesExpense'] })
            queryClient.invalidateQueries({ queryKey: ['categoriesBalance'] })
        },
        onError: (error) => {
            captureEntityMutation({ entity: 'category', operation: 'create', result: 'failure', error })
        },
    })
}

export const useUpdateCategory = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ id, data }: { id: number; data: CategoryInput }) => {
            const dataToSend = {
                name: data.name,
                ...(data.description !== undefined && { description: data.description }),
                ...(data.icon_id !== undefined && { icon_id: data.icon_id }),
                ...(data.color_id !== undefined && { color_id: data.color_id })
            }
            const res = await apiClient.patch(`/categories/${id}`, dataToSend)
            return res.data
        },
        onSuccess: () => {
            captureEntityMutation({ entity: 'category', operation: 'update', result: 'success' })
            queryClient.invalidateQueries({ queryKey: ['categories'] })
            queryClient.invalidateQueries({ queryKey: ['categoriesExpense'] })
            queryClient.invalidateQueries({ queryKey: ['categoriesBalance'] })
        },
        onError: (error) => {
            captureEntityMutation({ entity: 'category', operation: 'update', result: 'failure', error })
        },
    })
}

export const useDeleteCategory = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (categoryId: number) => {
            const res = await apiClient.delete(`/categories/${categoryId}`)
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] })
            queryClient.invalidateQueries({ queryKey: ['categoriesExpense'] })
            queryClient.invalidateQueries({ queryKey: ['categoriesBalance'] })
        },
    })
}
